import {
  SERVICE_BILLING_MODES,
  SERVICE_BILLING_STATUSES,
  SERVICE_COLLECTION_STATUSES,
  SERVICE_FOLLOW_UP_STATUSES,
  SERVICE_JOB_ORIGINS,
  SERVICE_JOB_SORT_FIELDS,
  SERVICE_JOB_STATUSES,
  SERVICE_JOB_VIEWS,
  SERVICE_PAYMENT_STATUSES,
  SERVICE_PRIORITIES,
  SERVICE_WAITING,
} from '@sahab/contracts'
import { z } from 'zod'

type JobSortField = (typeof SERVICE_JOB_SORT_FIELDS)[number]
export type ServiceJobSort = JobSortField | `-${JobSortField}`

export const SERVICE_PAGE_SIZES = [25, 50, 100] as const

export function isServiceJobSort(value: string): value is ServiceJobSort {
  const field = value.startsWith('-') ? value.slice(1) : value
  return (SERVICE_JOB_SORT_FIELDS as readonly string[]).includes(field)
}

const uuid = z.uuid().optional().catch(undefined)
const flag = z.boolean().optional().catch(undefined)
const page = z.number().int().min(1).optional().catch(undefined)
const pageSize = z
  .number()
  .refine((n): n is (typeof SERVICE_PAGE_SIZES)[number] =>
    (SERVICE_PAGE_SIZES as readonly number[]).includes(n),
  )
  .optional()
  .catch(undefined)

/**
 * The job register (`/service/jobs`) lives in the URL: view tab, filters, sort, page, and the New job
 * sheet. Keys are prefixed where another list uses the same name differently (`jobStatus`).
 */
export const serviceJobSearchSchema = z.object({
  view: z.enum(SERVICE_JOB_VIEWS).optional().catch(undefined),
  page,
  pageSize,
  sort: z.string().refine(isServiceJobSort).optional().catch(undefined),
  q: z.coerce.string().trim().max(200).optional().catch(undefined),
  jobStatus: z.enum(SERVICE_JOB_STATUSES).optional().catch(undefined),
  billing: z.enum(SERVICE_BILLING_STATUSES).optional().catch(undefined),
  payment: z.enum(SERVICE_PAYMENT_STATUSES).optional().catch(undefined),
  priority: z.enum(SERVICE_PRIORITIES).optional().catch(undefined),
  teamId: uuid,
  customerId: uuid,
  overdue: flag,
  waiting: z.enum(SERVICE_WAITING).optional().catch(undefined),
  followUpPending: flag,
  collectionsAwaiting: flag,
  billingMode: z.enum(SERVICE_BILLING_MODES).optional().catch(undefined),
  /** C3: planned jobs (made by a contract plan) or requests. */
  origin: z.enum(SERVICE_JOB_ORIGINS).optional().catch(undefined),
  /** Opens the New job sheet (with `customerId` prefilled when given). */
  new: flag,
  /** With `new`: the sheet starts as a copy of this job ("Repeat job"), its preferred date left empty. */
  repeat: uuid,
})
export type ServiceJobSearch = z.infer<typeof serviceJobSearchSchema>

/** The job page: which section is open (`money` from a collection notification). */
export const serviceJobPageSearchSchema = z.object({
  section: z.enum(['scope', 'visits', 'money', 'files', 'tasks', 'history']).optional().catch(undefined),
  /** A collection to highlight (the accountant's notification opens it with its invoice). */
  collection: uuid,
  /** Opens the schedule dialog (New job › "Book a visit"). */
  schedule: flag,
  /** Opens the reschedule dialog for this visit (New job › "Visit booked" › Change). */
  reschedule: uuid,
})
export type ServiceJobPageSearch = z.infer<typeof serviceJobPageSearchSchema>

/** The scheduling board: the first of the seven days shown (none: today) and the team filter. */
export const serviceBoardSearchSchema = z.object({
  week: z.iso.date().optional().catch(undefined),
  teamId: uuid,
})
export type ServiceBoardSearch = z.infer<typeof serviceBoardSearchSchema>

/** My visits: the day shown. */
export const serviceMyVisitsSearchSchema = z.object({
  date: z.iso.date().optional().catch(undefined),
})

export const serviceFollowUpsSearchSchema = z.object({
  fuStatus: z.enum(SERVICE_FOLLOW_UP_STATUSES).optional().catch(undefined),
  page,
})
export type ServiceFollowUpsSearch = z.infer<typeof serviceFollowUpsSearchSchema>

export const serviceCollectionsSearchSchema = z.object({
  collectionStatus: z.enum(SERVICE_COLLECTION_STATUSES).optional().catch(undefined),
  page,
  /** The collection open in the verification panel. */
  collection: uuid,
})
export type ServiceCollectionsSearch = z.infer<typeof serviceCollectionsSearchSchema>
